import { Fragment, useCallback, useEffect, useState } from 'react';
import { Title, useNotify } from 'react-admin';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import { calendarDate, dateTimeNoSeconds } from '../../shared/dateTime';
import { FeaturesLoading, useFeatureState } from '../featureFlags/FeatureGate';
import {
  DATE_BASES,
  FEATURE_CLIENT_REPORT_CONFIG,
  FEATURE_CLIENT_REPORT_QUEUE,
  FILE_FORMATS,
  FREQUENCIES,
  REPORT_COLUMNS,
  SUGGESTED_COLUMNS,
  WEEKDAYS,
  createConfig,
  fetchConfigHistory,
  fetchConfigs,
  fetchOwners,
  prepareNow,
  searchContractors,
  setConfigDisabled,
  toErrorMessage,
  updateConfig,
  type ConfigChange,
  type ConfigInput,
  type ReportConfig,
  type ReportLocation,
} from './clientReportsApi';

/** Pusty formularz nowej konfiguracji — układ makiety, bez wartości za CS. */
export function emptyForm(): ConfigInput {
  return {
    name: '',
    dateBasis: 'loading',
    rangeStartDays: 0,
    rangeEndDays: 6,
    locations: [],
    columns: [...SUGGESTED_COLUMNS],
    frequency: 'daily',
    weekday: null,
    sendTime: '',
    recipients: '',
    fileFormat: 'xlsx',
    ownerId: null,
    validFrom: '',
    reason: '',
  };
}

function formOf(c: ReportConfig): ConfigInput {
  return {
    name: c.name,
    dateBasis: c.dateBasis,
    rangeStartDays: c.rangeStartDays,
    rangeEndDays: c.rangeEndDays,
    locations: c.locations.map((l) => ({ ...l })),
    columns: [...c.columns],
    frequency: c.frequency,
    weekday: c.weekday,
    sendTime: c.sendTime,
    recipients: c.recipients ?? '',
    fileFormat: c.fileFormat,
    ownerId: c.owner?.id ?? null,
    validFrom: '',
    reason: '',
  };
}

interface ConfigDialogProps {
  open: boolean;
  config: ReportConfig | null;
  onClose: () => void;
  onSaved: () => void;
}

/** Formularz konfiguracji (nowa albo zmiana) — powód obowiązkowy. */
export const ConfigDialog = ({ open, config, onClose, onSaved }: ConfigDialogProps) => {
  const notify = useNotify();
  const [form, setForm] = useState<ConfigInput>(emptyForm());
  const [contractor, setContractor] = useState<{ id: number; legalName: string } | null>(null);
  const [search, setSearch] = useState('');
  const [found, setFound] = useState<Array<{ id: number; legalName: string }>>([]);
  const [owners, setOwners] = useState<Array<{ id: number; username: string }>>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(config ? formOf(config) : emptyForm());
    setContractor(config ? { id: config.contractorId, legalName: config.clientName } : null);
    setSearch('');
    setFound([]);
    fetchOwners()
      .then(setOwners)
      .catch(() => setOwners([]));
  }, [open, config]);

  const set = <K extends keyof ConfigInput>(key: K, value: ConfigInput[K]) => setForm((f) => ({ ...f, [key]: value }));
  const toggleColumn = (id: string) =>
    set('columns', form.columns.includes(id) ? form.columns.filter((c) => c !== id) : [...form.columns, id]);
  const setLocation = (i: number, patch: Partial<ReportLocation>) =>
    set(
      'locations',
      form.locations.map((l, n) => (n === i ? { ...l, ...patch } : l)),
    );

  const find = async () => {
    try {
      setFound(await searchContractors(search.trim()));
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    }
  };

  const canSave =
    contractor !== null &&
    form.name.trim() !== '' &&
    /^\d{2}:\d{2}$/.test(form.sendTime) &&
    form.columns.length > 0 &&
    (form.frequency !== 'weekly' || form.weekday !== null) &&
    form.reason.trim() !== '';

  const save = async () => {
    setBusy(true);
    const payload: ConfigInput = {
      ...form,
      locations: form.locations
        .map((l) => ({ country: l.country.trim().toUpperCase(), city: l.city?.trim() || null }))
        .filter((l) => l.country !== ''),
      weekday: form.frequency === 'weekly' ? form.weekday : null,
      reason: form.reason.trim(),
    };
    try {
      if (config) await updateConfig(config.id, payload);
      else await createConfig({ ...payload, contractorId: contractor!.id });
      notify(config ? 'Zapisano zmianę konfiguracji' : 'Zapisano konfigurację raportu', { type: 'success' });
      onSaved();
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{config ? 'Zmień konfigurację raportu' : 'Nowa konfiguracja raportu klienta'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }} data-testid="client-report-config-form">
          {config ? (
            <Typography variant="body2">Klient: {config.clientName}</Typography>
          ) : contractor ? (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Typography variant="body2" data-testid="client-report-config-client">
                Klient: {contractor.legalName}
              </Typography>
              <Button size="small" onClick={() => setContractor(null)}>
                Zmień
              </Button>
            </Stack>
          ) : (
            <Box>
              <Stack direction="row" spacing={1}>
                <TextField
                  label="Szukaj klienta (nazwa, NIP)"
                  size="small"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void find();
                  }}
                  slotProps={{ htmlInput: { 'data-testid': 'client-report-config-search' } }}
                />
                <Button onClick={() => void find()} disabled={search.trim() === ''}>
                  Szukaj
                </Button>
              </Stack>
              {found.map((c) => (
                <Button
                  key={c.id}
                  size="small"
                  sx={{ display: 'block', textAlign: 'left' }}
                  onClick={() => setContractor(c)}
                  data-testid={`client-report-config-pick-${c.id}`}
                >
                  {c.legalName}
                </Button>
              ))}
            </Box>
          )}
          <TextField
            label="Nazwa raportu"
            size="small"
            required
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            helperText="np. Plan dostaw tygodniowy, Raport załadunków dziennych"
            slotProps={{ htmlInput: { 'data-testid': 'client-report-config-name' } }}
          />
          <Box
            sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 180px), 1fr))' }}
          >
            <TextField
              select
              size="small"
              label="Zakres według"
              value={form.dateBasis}
              onChange={(e) => set('dateBasis', e.target.value)}
            >
              {DATE_BASES.map((b) => (
                <MenuItem key={b.id} value={b.id}>
                  {b.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              type="number"
              size="small"
              label="Od (dni od dnia raportu)"
              value={form.rangeStartDays}
              onChange={(e) => set('rangeStartDays', Number(e.target.value))}
              slotProps={{ htmlInput: { min: -31, max: 31 } }}
            />
            <TextField
              type="number"
              size="small"
              label="Do (dni od dnia raportu)"
              value={form.rangeEndDays}
              onChange={(e) => set('rangeEndDays', Number(e.target.value))}
              slotProps={{ htmlInput: { min: -31, max: 31 } }}
            />
            <TextField
              select
              size="small"
              label="Częstotliwość"
              value={form.frequency}
              onChange={(e) => set('frequency', e.target.value)}
              slotProps={{ htmlInput: { 'data-testid': 'client-report-config-frequency' } }}
            >
              {FREQUENCIES.map((f) => (
                <MenuItem key={f.id} value={f.id}>
                  {f.name}
                </MenuItem>
              ))}
            </TextField>
            {form.frequency === 'weekly' ? (
              <TextField
                select
                size="small"
                label="Dzień tygodnia"
                value={form.weekday ?? ''}
                onChange={(e) => set('weekday', Number(e.target.value))}
              >
                {WEEKDAYS.map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.name}
                  </MenuItem>
                ))}
              </TextField>
            ) : null}
            <TextField
              type="time"
              size="small"
              label="Godzina wysyłki"
              required
              value={form.sendTime}
              onChange={(e) => set('sendTime', e.target.value)}
              slotProps={{ inputLabel: { shrink: true }, htmlInput: { 'data-testid': 'cs-konfig-godzina' } }}
            />
            <TextField
              select
              size="small"
              label="Format pliku"
              value={form.fileFormat}
              onChange={(e) => set('fileFormat', e.target.value)}
            >
              {FILE_FORMATS.map((f) => (
                <MenuItem key={f.id} value={f.id}>
                  {f.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Właściciel"
              value={form.ownerId ?? ''}
              onChange={(e) => set('ownerId', e.target.value === '' ? null : Number(e.target.value))}
            >
              <MenuItem value="">nieustalony</MenuItem>
              {owners.map((o) => (
                <MenuItem key={o.id} value={o.id}>
                  {o.username}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              type="date"
              size="small"
              label="Obowiązuje od"
              value={form.validFrom}
              onChange={(e) => set('validFrom', e.target.value)}
              helperText={config ? `teraz: od ${calendarDate(config.validFrom)}` : 'puste — od dziś'}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Box>
          <TextField
            label="Adresaci"
            size="small"
            value={form.recipients}
            onChange={(e) => set('recipients', e.target.value)}
            helperText="adresy e-mail oddzielone średnikiem — trafią do szkicu wiadomości; system nic nie wysyła"
            slotProps={{ htmlInput: { 'data-testid': 'client-report-config-recipients' } }}
          />
          <Box>
            <Typography variant="subtitle2">Lokalizacje (puste — wszystkie)</Typography>
            {form.locations.map((l, i) => (
              <Stack key={i} direction="row" spacing={1} sx={{ mt: 1, alignItems: 'center' }}>
                <TextField
                  size="small"
                  label="Kraj"
                  value={l.country}
                  onChange={(e) => setLocation(i, { country: e.target.value })}
                  sx={{ width: 90 }}
                  slotProps={{ htmlInput: { maxLength: 2 } }}
                />
                <TextField
                  size="small"
                  label="Miejscowość (puste — cały kraj)"
                  value={l.city ?? ''}
                  onChange={(e) => setLocation(i, { city: e.target.value })}
                />
                <IconButton
                  aria-label="Usuń lokalizację"
                  onClick={() =>
                    set(
                      'locations',
                      form.locations.filter((_, n) => n !== i),
                    )
                  }
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </Stack>
            ))}
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => set('locations', [...form.locations, { country: '', city: null }])}
            >
              Dodaj lokalizację
            </Button>
          </Box>
          <Box>
            <Typography variant="subtitle2">Zakres pól</Typography>
            <Typography variant="caption" color="text.secondary">
              Kierowca i nr terminala Trimble — tylko gdy klient ich wymaga.
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 240px), 1fr))' }}>
              {REPORT_COLUMNS.map((c) => (
                <FormControlLabel
                  key={c.id}
                  control={
                    <Checkbox
                      size="small"
                      checked={form.columns.includes(c.id)}
                      onChange={() => toggleColumn(c.id)}
                      data-testid={`client-report-col-${c.id}`}
                    />
                  }
                  label={c.name}
                />
              ))}
            </Box>
          </Box>
          <Alert severity="info">Wysyłka automatyczna: nie — raport akceptuje i wysyła CS ze swojej poczty.</Alert>
          <TextField
            label="Powód"
            size="small"
            required
            value={form.reason}
            onChange={(e) => set('reason', e.target.value)}
            helperText="np. mail klienta z prośbą o raport"
            slotProps={{ htmlInput: { 'data-testid': 'client-report-config-reason' } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Wróć</Button>
        <Button
          variant="contained"
          disabled={!canSave || busy}
          onClick={() => void save()}
          data-testid="client-report-config-save"
        >
          Zapisz konfigurację
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** Okno powodu — wyłączenie albo włączenie konfiguracji. */
const ReasonDialog = ({
  title,
  label,
  open,
  onClose,
  onConfirm,
}: {
  title: string;
  label: string;
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void>;
}) => {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setReason('');
  }, [open]);
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <TextField
          sx={{ mt: 1 }}
          fullWidth
          label="Powód"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          slotProps={{ htmlInput: { 'data-testid': 'client-report-config-toggle-reason' } }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Wróć</Button>
        <Button
          variant="contained"
          disabled={reason.trim() === '' || busy}
          onClick={async () => {
            setBusy(true);
            try {
              await onConfirm(reason.trim());
            } finally {
              setBusy(false);
            }
          }}
        >
          {label}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const History = ({ configId }: { configId: number }) => {
  const [items, setItems] = useState<ConfigChange[] | null>(null);
  useEffect(() => {
    fetchConfigHistory(configId)
      .then(setItems)
      .catch(() => setItems([]));
  }, [configId]);
  if (!items) return <Typography variant="caption">Wczytuję historię…</Typography>;
  if (items.length === 0) return <Typography variant="caption">Bez historii.</Typography>;
  return (
    <Box data-testid={`client-report-config-history-${configId}`}>
      {items.map((h) => (
        <Box key={h.id} sx={{ py: 0.5, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="body2">
            {dateTimeNoSeconds(h.changedAt)} · {h.changedByName ?? '—'} · {h.kindLabel} · powód: {h.reason}
          </Typography>
          {h.changes.length > 0 && h.kind !== 'created' ? (
            <Typography variant="caption" color="text.secondary" component="div">
              {h.changes.map((c) => `${c.label}: ${c.before ?? '—'} → ${c.after ?? '—'}`).join('; ')}
            </Typography>
          ) : null}
        </Box>
      ))}
    </Box>
  );
};

/**
 * Konfiguracja raportów klienta (prace 26.10.2026, pakiet P2, S-26.10-04,
 * przełącznik `clientReportConfig`; makieta p. Mariusza, ekran 9). Lista
 * konfiguracji, nowa i zmiana z powodem, wyłączenie i włączenie, historia;
 * „Przygotuj teraz” przy włączonej kolejce raportów (S-26.10-05). Reguły
 * rozstrzyga API (`client-reports/report-config.ts`).
 */
export const ClientReportConfigScreen = () => {
  const on = useFeatureState(FEATURE_CLIENT_REPORT_CONFIG);
  const queueOn = useFeatureState(FEATURE_CLIENT_REPORT_QUEUE) === true;
  const notify = useNotify();
  const [items, setItems] = useState<ReportConfig[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [includeDisabled, setIncludeDisabled] = useState(false);
  const [editing, setEditing] = useState<ReportConfig | null | 'new'>(null);
  const [toggling, setToggling] = useState<ReportConfig | null>(null);
  const [historyOf, setHistoryOf] = useState<number | null>(null);

  const load = useCallback(() => {
    fetchConfigs(includeDisabled)
      .then((list) => {
        setItems(list);
        setError(null);
      })
      .catch((e) => setError(toErrorMessage(e)));
  }, [includeDisabled]);

  useEffect(() => {
    if (on === true) load();
  }, [on, load]);

  if (on === undefined) return <FeaturesLoading />;
  if (!on) return null;

  const runNow = async (c: ReportConfig) => {
    try {
      const r = await prepareNow(c.id);
      notify(`Raport przygotowany: ${r.statusLabel} — w kolejce „Raporty dla klientów”`, { type: 'success' });
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    }
  };

  return (
    <Box sx={{ p: 1 }} data-testid="client-report-configs">
      <Title title="Konfiguracja raportów klienta" />
      <Card variant="outlined">
        <CardContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Co i kiedy dostaje klient: zakres zleceń, lokalizacje, pola, częstotliwość i godzina wysyłki. Konfigurację
            wpisuje CS — formatów klientów nie zgadujemy. Raport powstaje w kolejce „Raporty dla klientów”; wysyła go
            CS ze swojej poczty.
          </Typography>
          <Stack direction="row" spacing={2} sx={{ mb: 1, alignItems: 'center' }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setEditing('new')}
              data-testid="client-report-config-new"
            >
              Nowa konfiguracja
            </Button>
            <FormControlLabel
              control={<Checkbox checked={includeDisabled} onChange={(e) => setIncludeDisabled(e.target.checked)} />}
              label="Pokaż wyłączone"
            />
          </Stack>
          {error ? <Alert severity="error">{error}</Alert> : null}
          {items && items.length === 0 ? (
            <Typography variant="body2" color="text.secondary" data-testid="client-report-configs-empty">
              Brak konfiguracji raportów — dodaj pierwszą przyciskiem „Nowa konfiguracja”.
            </Typography>
          ) : null}
          {items && items.length > 0 ? (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Klient i raport</TableCell>
                    <TableCell>Zakres i lokalizacje</TableCell>
                    <TableCell>Pola</TableCell>
                    <TableCell>Harmonogram</TableCell>
                    <TableCell>Właściciel</TableCell>
                    <TableCell>Stan</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((c) => (
                    <Fragment key={c.id}>
                      <TableRow data-testid={`client-report-config-row-${c.id}`}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {c.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {c.clientName}
                            {c.clientTmsId ? ` · ID ${c.clientTmsId}` : ''}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {c.dateBasisLabel}, {c.rangeLabel}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Lokalizacje: {c.locationsLabel}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 260 }}>
                          <Typography variant="caption">{c.columnsLabel}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{c.scheduleLabel}</Typography>
                          <Typography variant="caption" color="text.secondary" component="div">
                            {c.fileFormatLabel} · wysyłka automatyczna: nie
                          </Typography>
                          {c.nextDueAt ? (
                            <Typography variant="caption" color="text.secondary" component="div">
                              najbliższy termin: {dateTimeNoSeconds(c.nextDueAt)}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" data-testid={`client-report-config-owner-${c.id}`}>
                            {c.owner?.name ?? 'właściciel nieustalony'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            obowiązuje od {calendarDate(c.validFrom)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {c.disabled ? (
                            <Chip
                              size="small"
                              label="wyłączona"
                              title={c.disabledReason ? `powód: ${c.disabledReason}` : undefined}
                            />
                          ) : (
                            <Chip size="small" color="success" label="włączona" />
                          )}
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }} useFlexGap>
                            <Button size="small" onClick={() => setEditing(c)}>
                              Zmień
                            </Button>
                            {queueOn && !c.disabled ? (
                              <Button
                                size="small"
                                onClick={() => void runNow(c)}
                                data-testid={`client-report-prepare-now-${c.id}`}
                              >
                                Przygotuj teraz
                              </Button>
                            ) : null}
                            <Button size="small" onClick={() => setToggling(c)}>
                              {c.disabled ? 'Włącz' : 'Wyłącz'}
                            </Button>
                            <Button size="small" onClick={() => setHistoryOf(historyOf === c.id ? null : c.id)}>
                              Historia
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                      {historyOf === c.id ? (
                        <TableRow>
                          <TableCell colSpan={7}>
                            <History configId={c.id} />
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </Fragment>
                  ))}
                </TableBody>
              </Table>
            </Box>
          ) : null}
        </CardContent>
      </Card>
      <ConfigDialog
        open={editing !== null}
        config={editing === 'new' ? null : editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          load();
        }}
      />
      <ReasonDialog
        open={toggling !== null}
        title={toggling?.disabled ? 'Włącz konfigurację raportu' : 'Wyłącz konfigurację raportu'}
        label={toggling?.disabled ? 'Włącz' : 'Wyłącz'}
        onClose={() => setToggling(null)}
        onConfirm={async (reason) => {
          if (!toggling) return;
          try {
            await setConfigDisabled(toggling.id, !toggling.disabled, reason);
            notify(toggling.disabled ? 'Konfiguracja włączona' : 'Konfiguracja wyłączona', { type: 'success' });
            setToggling(null);
            load();
          } catch (e) {
            notify(toErrorMessage(e), { type: 'error' });
          }
        }}
      />
    </Box>
  );
};
